import React, { useState } from 'react';
import { 
  ArrowLeft, 
  AlertTriangle, 
  Upload, 
  MessageSquare, 
  Zap, 
  Calendar, 
  ChevronRight 
} from 'lucide-react';
import styled from 'styled-components';
import { PageHeader, PageTitle, BackButton } from './TeacherJourney';
import ResourceGenerator from './ResourceGenerator';
import MessageModal from '../../shared/MessageModal';
import { useUser } from '../../../contexts/UserContext';
import { UserRole } from '@/types/message';

export const StudentDetailsContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

export const StudentProfileCard = styled.div`
  background-color: #f3f0ff;
  border-radius: 0.75rem;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border: 1px solid #e9d5ff;
`;

export const StudentProfileHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
`;

export const StudentAvatar = styled.div`
  width: 3rem;
  height: 3rem;
  border-radius: 9999px;
  background-color: #d8b4fe;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 1.25rem;
  margin-right: 1rem;
`;

export const StudentInfo = styled.div``;

export const StudentName = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  color: #581c87;
`;

export const StudentMeta = styled.p`
  font-size: 0.875rem;
  color: #6b7280;
`;

export const StudentStatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
`;

export const StudentStatCard = styled.div`
  background-color: white;
  border-radius: 0.5rem;
  padding: 0.75rem;
  border: 1px solid #e5e7eb;
`;

export const StatLabel = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
  margin-bottom: 0.25rem;
`;

export const StatContent = styled.div`
  display: flex;
  align-items: center;
`;

export const ProgressBar = styled.div`
  flex: 1;
  height: 0.5rem;
  background-color: #e5e7eb;
  border-radius: 9999px;
  overflow: hidden;
  margin-right: 0.75rem;
`;

export const ProgressFill = styled.div<{ progress: number }>`
  height: 100%;
  background-color: ${({ progress }) => {
    if (progress >= 80) return '#059669';
    if (progress >= 70) return '#2563eb';
    if (progress >= 60) return '#d97706';
    return '#dc2626';
  }};
  width: ${({ progress }) => `${progress}%`};
  border-radius: 9999px;
`;

export const ProgressValue = styled.div`
  font-weight: 600;
  color: #111827;
`;

export const InterventionCard = styled.div`
  background-color: #fff1f2;
  border-radius: 0.75rem;
  padding: 1.25rem;
  margin-bottom: 1.5rem;
  border: 1px solid #fecaca;
`;

export const InterventionHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 0.75rem;
  font-weight: 600;
  color: #be123c;
`;

export const InterventionMessage = styled.p`
  color: #4b5563;
  margin-bottom: 1rem;
  font-size: 0.875rem;
`;

export const ScheduleButton = styled.button`
  background-color: #fee2e2;
  color: #be123c;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  font-weight: 500;
  font-size: 0.875rem;
  border: none;
  cursor: pointer;
  
  &:hover {
    background-color: #fecaca;
  }
`;

export const SectionTitle = styled.h3`
  font-weight: 700;
  color: #111827;
  margin-bottom: 1rem;
`;

export const ActivityCard = styled.div`
  background-color: white;
  border-radius: 0.5rem;
  padding: 1rem;
  margin-bottom: 0.75rem;
  border: 1px solid #e5e7eb;
`;

export const ActivityHeader = styled.div`
  display: flex;
  align-items: start;
`;

export const ActivityIcon = styled.div<{ variant: 'blue' | 'green' }>`
  padding: 0.5rem;
  border-radius: 9999px;
  background-color: ${({ variant }) => variant === 'blue' ? '#eff6ff' : '#ecfdf5'};
  margin-right: 0.75rem;
  
  svg {
    color: ${({ variant }) => variant === 'blue' ? '#2563eb' : '#059669'};
  }
`;

export const ActivityContent = styled.div`
  flex: 1;
`;

export const ActivityTitle = styled.h4`
  font-weight: 600;
  color: #111827;
  margin-bottom: 0.25rem;
`;

export const ActivityDescription = styled.p`
  font-size: 0.875rem;
  color: #6b7280;
  margin-bottom: 0.5rem;
`;

export const ActivityDetails = styled.div`
  background-color: #f9fafb;
  border-radius: 0.375rem;
  padding: 0.75rem;
  margin-top: 0.5rem;
  font-size: 0.875rem;
`;

export const ActivityMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
`;

export const ActivityStatus = styled.span<{ status: 'success' | 'error' | 'warning' }>`
  color: ${({ status }) => {
    switch (status) {
      case 'success':
        return '#059669';
      case 'error':
        return '#dc2626';
      case 'warning':
        return '#d97706';
    }
  }};
  font-weight: 500;
`;

export const ActivityFeedback = styled.div`
  font-size: 0.75rem;
  color: #4b5563;
  
  span {
    margin-right: 0.5rem;
  }
`;

export const ViewButton = styled.button`
  color: #7c3aed;
  font-size: 0.875rem;
  font-weight: 500;
  margin-top: 0.5rem;
  text-align: right;
  display: block;
  width: 100%;
  
  &:hover {
    text-decoration: underline;
  }
`;

export const ActionButton = styled.button`
  width: 100%;
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  text-align: left;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: white;
  border: 1px solid #e5e7eb;
  margin-bottom: 0.75rem;
  
  &:hover {
    background-color: #f9fafb;
  }
`;

export const ActionContent = styled.div`
  display: flex;
  align-items: center;
`;

export const ActionIcon = styled.div<{ variant: 'purple' | 'blue' | 'amber' }>`
  padding: 0.5rem;
  border-radius: 9999px;
  background-color: ${({ variant }) => {
    switch (variant) {
      case 'purple':
        return '#f3f0ff';
      case 'blue':
        return '#eff6ff';
      case 'amber':
        return '#fffbeb';
    }
  }};
  margin-right: 0.75rem;
  
  svg {
    color: ${({ variant }) => {
      switch (variant) {
        case 'purple':
          return '#7c3aed';
        case 'blue':
          return '#2563eb';
        case 'amber':
          return '#d97706';
      }
    }};
  }
`;

export const ActionText = styled.span`
  font-weight: 500;
  color: #111827;
`;

interface StudentDetailsProps {
  studentData: {
    _id: string;
    firstName: string;
    lastName: string;
    grade: string;
    email: string;
  };
  onBack: () => void;
}

const StudentDetails: React.FC<StudentDetailsProps> = ({ studentData, onBack }) => {
  const [showResourceGen, setShowResourceGen] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const { profile } = useUser();

  const teacherUser = {
    id: profile?._id || '',
    name: profile ? `${profile.firstName} ${profile.lastName}` : 'Your Name',
    role: 'teacher' as UserRole,
  };
  const studentUser = {
    id: studentData._id,
    name: `${studentData.firstName} ${studentData.lastName}`,
    role: 'student' as UserRole,
  };

  return (
    <StudentDetailsContainer>
      <PageHeader>
        <BackButton onClick={onBack}>
          <ArrowLeft size={20} />
        </BackButton>
        <PageTitle>Student Details</PageTitle>
      </PageHeader>

      <StudentProfileCard>
        <StudentProfileHeader>
          <StudentAvatar>
            {studentData.firstName[0]}{studentData.lastName[0]}
          </StudentAvatar>
          <StudentInfo>
            <StudentName>{studentData.firstName} {studentData.lastName}</StudentName>
            <StudentMeta>Grade {studentData.grade} • {studentData.email}</StudentMeta>
          </StudentInfo>
        </StudentProfileHeader>

        <StudentStatsGrid>
          <StudentStatCard>
            <StatLabel>Grade Level</StatLabel>
            <StatContent>
              <span>{studentData.grade}</span>
            </StatContent>
          </StudentStatCard>
          <StudentStatCard>
            <StatLabel>Email</StatLabel>
            <StatContent>
              <span>{studentData.email}</span>
            </StatContent>
          </StudentStatCard>
        </StudentStatsGrid>
      </StudentProfileCard>

      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem' }}>
        <button
          onClick={() => setShowResourceGen(true)}
          style={{
            background: '#7c3aed',
            color: '#fff',
            border: 'none',
            borderRadius: '0.375rem',
            padding: '0.5rem 1rem',
            fontWeight: 500,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          <Zap size={16} />
          Generate Resources
        </button>
        <button
          onClick={() => setShowMessageModal(true)}
          style={{
            background: '#f3f4f6',
            color: '#374151',
            border: 'none',
            borderRadius: '0.375rem',
            padding: '0.5rem 1rem',
            fontWeight: 500,
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
        >
          <MessageSquare size={16} />
          Send Message
        </button>
      </div>

      {showResourceGen && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.15)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <div style={{
            background: '#fff',
            borderRadius: 12,
            boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
            maxWidth: 650,
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            padding: '1rem'
          }}>
            <ResourceGenerator
              student={studentData}
              subject="Current Subject"
              onBack={() => setShowResourceGen(false)}
              onGenerate={() => {}}
            />
          </div>
        </div>
      )}

      {showMessageModal && (
        <MessageModal
          isOpen={showMessageModal}
          onClose={() => setShowMessageModal(false)}
          sender={teacherUser}
          receiver={studentUser}
        />
      )}
    </StudentDetailsContainer>
  );
};

export default StudentDetails; 